/**
 * CommentSidebar - Inline comment panel for photo viewer
 *
 * Displays comments on a media item with create, delete, and reply functionality.
 * Supports 1-level reply threading. Renders inline (not as a dialog).
 */
import { useState, useRef, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  useApiClient,
  useComments,
  useDeleteComment,
  useEditComment,
  useCommentHistory,
  useMembers,
  formatRelativeTime,
  parseMentions,
  type Comment,
} from '@wovendays/api-client';
import { useTranslation } from '@wovendays/i18n';
import { useAuth } from '@/contexts/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MentionInput } from '@/components/MentionInput';
import { X, Reply, Pencil, Check, ChevronDown, ChevronUp } from 'lucide-react';

interface CommentSidebarProps {
  mediaId: string;
  familyId?: string;
  viewerRole?: 'owner' | 'admin' | 'contributor' | 'viewer';
}

export function CommentSheet({ mediaId, familyId, viewerRole }: CommentSidebarProps) {
  const [content, setContent] = useState('');
  const [replyingTo, setReplyingTo] = useState<{ id: string; authorName: string } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [historyOpenId, setHistoryOpenId] = useState<string | null>(null);
  const { t } = useTranslation('photo');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const commentsListRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useComments(mediaId);
  const { data: membersData } = useMembers(familyId);

  const comments = data?.comments ?? [];
  const totalCount = comments.reduce((sum, c) => sum + 1 + (c.replies?.length ?? 0), 0);

  // Members for @mention autocomplete (exclude current user)
  const mentionMembers = (membersData?.members ?? [])
    .filter(m => m.userId !== user?.id)
    .map(m => ({ userId: m.userId, name: m.user.name }));

  const apiClient = useApiClient();

  // Comment creation with optimistic updates (needs local refs + user context)
  const createMutation = useMutation({
    mutationFn: (params: { content: string; parentId?: string }) =>
      apiClient.post<{ comment: Comment }>(`/api/media/${mediaId}/comments`, params),
    onMutate: async (params: { content: string; parentId?: string }) => {
      await queryClient.cancelQueries({ queryKey: ['comments', mediaId] });
      const previous = queryClient.getQueryData(['comments', mediaId]);

      const optimisticComment: Comment = {
        id: `optimistic-${Date.now()}`,
        mediaId,
        userId: user?.id ?? '',
        content: params.content,
        parentId: params.parentId ?? null,
        editedAt: null,
        createdAt: Date.now(),
        authorName: user?.name || 'You',
        replies: [],
      };

      queryClient.setQueryData(['comments', mediaId], (old: { comments: Comment[] } | undefined) => {
        const existing = old?.comments || [];
        if (params.parentId) {
          return {
            comments: existing.map((c: Comment) =>
              c.id === params.parentId
                ? { ...c, replies: [...(c.replies || []), optimisticComment] }
                : c
            ),
          };
        }
        return { comments: [...existing, optimisticComment] };
      });

      setTimeout(() => {
        commentsListRef.current?.scrollTo({ top: commentsListRef.current.scrollHeight, behavior: 'smooth' });
      }, 50);

      return { previous };
    },
    onError: (_err, _params, context) => {
      queryClient.setQueryData(['comments', mediaId], context?.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', mediaId] });
    },
  });

  const deleteCommentMutation = useDeleteComment(mediaId);
  const editCommentMutation = useEditComment(mediaId);

  const { data: historyData } = useCommentHistory(historyOpenId);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;
    createMutation.mutate({
      content: content.trim(),
      ...(replyingTo ? { parentId: replyingTo.id } : {}),
    });
    setContent('');
    setReplyingTo(null);
  };

  const handleReply = (comment: Comment) => {
    setReplyingTo({ id: comment.id, authorName: comment.authorName });
    inputRef.current?.focus();
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const renderComment = (comment: Comment, isReply = false) => {
    const isEditing = editingId === comment.id;
    const isOwnComment = comment.userId === user?.id;
    const canModerate = viewerRole === 'owner' || viewerRole === 'admin';
    const isOptimistic = comment.id.startsWith('optimistic');
    const showHistory = historyOpenId === comment.id;

    return (
      <div key={comment.id} className={`group ${isReply ? 'ml-6 border-l-2 border-muted pl-3' : ''}`}>
        <div className="flex items-start justify-between">
          <div>
            <span className="text-sm font-medium text-foreground">
              {comment.authorName}
            </span>
            <span className="ml-2 font-mono text-xs text-muted-foreground">
              {formatRelativeTime(comment.createdAt)}
            </span>
            {comment.editedAt && (
              <button
                onClick={() => setHistoryOpenId(showHistory ? null : comment.id)}
                className="ml-1 font-mono text-xs text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-0.5"
                aria-label={t('editHistory')}
              >
                {t('edited')}
                {showHistory ? <ChevronUp className="h-2.5 w-2.5" /> : <ChevronDown className="h-2.5 w-2.5" />}
              </button>
            )}
          </div>
          <div className="flex items-center gap-1">
            {isOwnComment && !isOptimistic && !isEditing && (
              <button
                onClick={() => {
                  setEditingId(comment.id);
                  setEditDraft(comment.content);
                }}
                className="opacity-0 group-hover:opacity-100 focus:opacity-100 rounded p-2 text-muted-foreground hover:text-foreground transition-opacity"
                aria-label={t('common:edit')}
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
            {!isReply && !isOptimistic && (
              <button
                onClick={() => handleReply(comment)}
                className="opacity-0 group-hover:opacity-100 focus:opacity-100 rounded p-2 text-muted-foreground hover:text-foreground transition-opacity"
                aria-label={t('common:reply')}
              >
                <Reply className="h-3.5 w-3.5" />
              </button>
            )}
            {(isOwnComment || canModerate) && !isOptimistic && (
              <button
                onClick={() => deleteCommentMutation.mutate(comment.id)}
                className="opacity-0 group-hover:opacity-100 focus:opacity-100 rounded p-2 text-muted-foreground hover:text-destructive transition-opacity"
                aria-label={t('common:delete')}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        {isEditing ? (
          <div className="mt-1 flex items-center gap-2">
            <Input
              type="text"
              value={editDraft}
              onChange={(e) => setEditDraft(e.target.value)}
              maxLength={1000}
              className="flex-1 text-sm"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (editDraft.trim()) {
                    editCommentMutation.mutate({ commentId: comment.id, content: editDraft.trim() }, { onSuccess: () => { setEditingId(null); setEditDraft(''); } });
                  }
                }
                if (e.key === 'Escape') {
                  setEditingId(null);
                  setEditDraft('');
                }
              }}
            />
            <button
              onClick={() => {
                if (editDraft.trim()) {
                  editCommentMutation.mutate({ commentId: comment.id, content: editDraft.trim() }, { onSuccess: () => { setEditingId(null); setEditDraft(''); } });
                }
              }}
              disabled={!editDraft.trim() || editCommentMutation.isPending}
              className="rounded p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-50"
              aria-label={t('common:save')}
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              onClick={() => { setEditingId(null); setEditDraft(''); }}
              className="rounded p-1.5 text-muted-foreground hover:text-foreground"
              aria-label={t('common:cancel')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <p className="mt-0.5 text-sm text-foreground/80">
            <CommentContent content={comment.content} />
          </p>
        )}
        {showHistory && historyData?.history && historyData.history.length > 0 && (
          <div className="mt-1.5 rounded-lg bg-muted/50 px-3 py-2 space-y-1.5">
            <p className="font-mono text-xs text-muted-foreground font-medium">{t('editHistory')}</p>
            {historyData.history.map((edit) => (
              <div key={edit.id} className="border-l-2 border-muted-foreground/20 pl-2">
                <p className="text-xs text-foreground/60">{edit.previousContent}</p>
                <span className="font-mono text-[10px] text-muted-foreground">
                  {formatRelativeTime(edit.editedAt)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col" aria-label={t('comments')}>
      {/* Header */}
      <div className="border-b px-4 py-3">
        <h3 className="font-serif font-bold text-foreground">
          {t('comments')} {totalCount > 0 && `(${totalCount})`}
        </h3>
      </div>

      {/* Comments list */}
      <div ref={commentsListRef} className="flex-1 overflow-y-auto px-4 py-3">
        {isLoading ? (
          <div className="flex justify-center py-4" role="status" aria-label={t('activity:loadingComments')}>
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted border-t-primary-600" />
          </div>
        ) : comments.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            {t('commentsEmpty')}
          </p>
        ) : (
          <div className="space-y-3">
            {comments.map((comment) => (
              <div key={comment.id}>
                {renderComment(comment)}
                {comment.replies?.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {comment.replies.map((reply) => renderComment(reply, true))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reply indicator */}
      {replyingTo && (
        <div className="flex items-center gap-2 border-t bg-muted/50 px-4 py-2">
          <Reply className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="flex-1 text-xs text-muted-foreground">
            {t('replyingTo')} <strong>{replyingTo.authorName}</strong>
          </span>
          <button
            onClick={cancelReply}
            className="rounded p-1 text-muted-foreground hover:text-foreground"
            aria-label={t('common:cancel')}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSubmit} className="border-t px-4 py-3">
        {createMutation.isError && (
          <p className="mb-2 text-center text-sm text-destructive">
            {t('failedToPost')}
          </p>
        )}
        <div className="flex gap-2">
          <label htmlFor="comment-input" className="sr-only">{t('addComment')}</label>
          <MentionInput
            ref={inputRef}
            value={content}
            onChange={setContent}
            members={mentionMembers}
            placeholder={replyingTo ? t('replyTo', { name: replyingTo.authorName }) : t('addComment')}
            maxLength={1000}
            onSubmit={() => { if (content.trim()) { createMutation.mutate({ content: content.trim(), ...(replyingTo ? { parentId: replyingTo.id } : {}) }); setContent(''); setReplyingTo(null); } }}
            disabled={createMutation.isPending}
          />
          <Button
            type="submit"
            disabled={!content.trim() || createMutation.isPending}
            className="rounded-full"
            size="sm"
          >
            {t('common:send')}
          </Button>
        </div>
      </form>
    </div>
  );
}

/** Renders comment content with styled @mentions */
function CommentContent({ content }: { content: string }) {
  const tokens = parseMentions(content);
  if (tokens.length === 1 && tokens[0]!.type === 'text') {
    return <>{content}</>;
  }
  return (
    <>
      {tokens.map((token, i) =>
        token.type === 'mention' ? (
          <span key={i} className="font-semibold text-primary">
            @{token.value}
          </span>
        ) : (
          <span key={i}>{token.value}</span>
        )
      )}
    </>
  );
}
